import LiquidText, { liquidTone } from '../components/LiquidText';
import { LiquidDialog } from '../components/LiquidUI';
import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/* ============================================================
   RISCOS E OPORTUNIDADES (SGI — ISO 9001/45001, 6.1.1)
   Estrutura fiel ao RG.06: risco/oportunidade por processo e
   contexto, decisão segundo os critérios, medidas e
   acompanhamento periódico (0 = atraso, 1 = decorre, 2 = concluído).
   ============================================================ */

/* A caixa das observações cresce até ao tamanho do texto. As observações dos
   riscos têm em média 970 caracteres, e a maior 1840 — numa caixa de duas
   linhas liam-se às fatias, a rolar. Tem teto (360px, perto de 16 linhas) e a
   partir daí rola por dentro, para uma observação comprida não empurrar os
   botões de Guardar para fora do ecrã. Continua a poder puxar-se à mão. */
const ajustarAltura = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight + 2, 360) + 'px';
};

const TIPOS = {
    risco: { label: 'Risco', bg: '#fee2e2', fg: '#991b1b' },
    oportunidade: { label: 'Oportunidade', bg: '#dcfce7', fg: '#166534' },
};
/* Significância — quanto o fator importa. Eixo independente da decisão:
   um risco pode ser significativo e ainda assim manter-se, se não for
   controlável. Critérios definidos na sequência da OM-2025-001. */
const SIGNIFICANCIA = {
    significativo: { label: 'Significativo', bg: '#fee2e2', fg: '#991b1b' },
    moderado: { label: 'Moderado', bg: '#fef3c7', fg: '#92400e' },
    nao_significativo: { label: 'Não significativo', bg: '#f1f5f9', fg: '#475569' },
};

const DECISOES = {
    mitigar: { label: 'Mitigar', bg: '#fef9c3', fg: '#854d0e' },
    partilhar: { label: 'Partilhar', bg: '#e0f2fe', fg: '#075985' },
    manter: { label: 'Manter', bg: '#f1f5f9', fg: '#475569' },
    prosseguir: { label: 'Prosseguir', bg: '#dcfce7', fg: '#166534' },
    nao_prosseguir: { label: 'Não prosseguir', bg: '#fee2e2', fg: '#991b1b' },
};
const ACOMP_LABEL = { 0: 'Atraso', 1: 'Decorre', 2: 'Concluído' };
const ACOMP_COR = {
    0: { bg: '#fee2e2', fg: '#991b1b' },
    1: { bg: '#fef9c3', fg: '#854d0e' },
    2: { bg: '#dcfce7', fg: '#166534' },
};

const fmtData = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('pt-PT'); } catch { return '—'; } };

const campoVazio = {
    processo: '', contexto: '', origem: '', descricao: '', tipo: 'risco', decisao: '',
    acoes: '', planeamento: '', recursos: '', prazo: '', monitorizacao: '',
    eficacia: '', significancia: '', observacoes: '',
};

/* ---------- SWOT (RG.06, folha 1) ----------
   A análise do contexto: forças e fraquezas são internas e não levam decisão;
   oportunidades e ameaças são externas e é nelas que se decide o que fazer.
   Cada uma pode dar origem ao registo de risco/oportunidade correspondente. */

const QUADRANTES = [
    { v: 'forca', l: 'Forças', icone: '💪', origem: 'Interna', positivo: true, bg: '#f0fdf4', bd: '#bbf7d0' },
    { v: 'fraqueza', l: 'Fraquezas', icone: '🔧', origem: 'Interna', positivo: false, bg: '#fef2f2', bd: '#fecaca' },
    { v: 'oportunidade', l: 'Oportunidades', icone: '🌱', origem: 'Externa', positivo: true, bg: '#eff6ff', bd: '#bfdbfe' },
    { v: 'ameaca', l: 'Ameaças', icone: '⚠️', origem: 'Externa', positivo: false, bg: '#fffbeb', bd: '#fde68a' },
];

function SwotLinhaModal({ linha, ano, onClose, onSaved }) {
    const novo = !linha?.id;
    const [form, setForm] = useState({
        quadrante: linha?.quadrante || 'forca',
        descricao: linha?.descricao || '',
        decisao: linha?.decisao || '',
        observacoes: linha?.observacoes || '',
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));
    // Os quatro quadrantes levam decisão: a auditoria determinou que forças e
    // fraquezas também são avaliadas como R&O. O conjunto de decisões segue a
    // natureza — positivo prossegue-se, negativo mitiga-se.
    const positivo = ['forca', 'oportunidade'].includes(form.quadrante);
    const decisoes = positivo
        ? [['prosseguir', 'Prosseguir'], ['nao_prosseguir', 'Não prosseguir']]
        : [['mitigar', 'Mitigar'], ['partilhar', 'Partilhar'], ['manter', 'Manter']];

    const guardar = async () => {
        if (!form.descricao.trim()) { setErr('Indique a descrição.'); return; }
        setSaving(true); setErr('');
        try {
            const payload = { ...form, ano };
            if (novo) await api.post('/sistema-integrado/swot', payload);
            else await api.put(`/sistema-integrado/swot/${linha.id}`, payload);
            onSaved(); onClose();
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao guardar'); }
        finally { setSaving(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 16px' }}>{novo ? 'Nova linha da SWOT' : 'Editar linha da SWOT'}</h3>
                <div style={{ marginBottom: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Quadrante</label>
                    <select value={form.quadrante} onChange={e => upd('quadrante', e.target.value)} style={{ ...inp, width: '100%' }}>
                        {QUADRANTES.map(q => (
                            <option key={q.v} value={q.v}>{q.l} · origem {q.origem.toLowerCase()}</option>
                        ))}
                    </select>
                </div>
                <div style={{ marginBottom: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Descrição</label>
                    <textarea value={form.descricao} onChange={e => upd('descricao', e.target.value)}
                        rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Decisão</label>
                    <select value={form.decisao} onChange={e => upd('decisao', e.target.value)} style={{ ...inp, width: '100%' }}>
                        <option value="">—</option>
                        {decisoes.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <p style={{ margin: '4px 0 0', fontSize: '0.74rem', color: '#94a3b8' }}>
                        Os critérios que fundamentam cada decisão estão na vista «Critérios».
                    </p>
                </div>
                <div>
                    <label data-liquid-label="true" style={lbl}>Observações</label>
                    <textarea value={form.observacoes} onChange={e => upd('observacoes', e.target.value)}
                        ref={ajustarAltura} onInput={e => ajustarAltura(e.target)}
                        rows={4} style={{ ...inp, width: '100%', resize: 'vertical', lineHeight: 1.5, minHeight: '6.5em' }} />
                </div>
                {err && <div style={{ color: '#e53e3e', marginTop: 10, fontSize: '0.85rem' }}>{err}</div>}
                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-primary="true" onClick={guardar} disabled={saving} style={btnPri}>{saving ? 'A guardar…' : 'Guardar'}</button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Criar / editar ---------- */
function RiscoModal({ risco, ano, onClose, onSaved }) {
    // Sem id é novo, mesmo vindo com campos preenchidos — é assim que a SWOT
    // consegue abrir este formulário já com a descrição e a decisão herdadas.
    const novo = !risco?.id;
    const [form, setForm] = useState(() => novo ? { ...campoVazio } : {
        ...campoVazio,
        ...Object.fromEntries(Object.keys(campoVazio).map(k => [k, risco[k] ?? ''])),
        prazo: risco?.prazo ? String(risco.prazo).substring(0, 10) : '',
        decisao: risco?.decisao || '',
        origem: risco?.origem || '',
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const guardar = async () => {
        if (!form.descricao.trim()) { setErr('Indique a descrição do risco/oportunidade.'); return; }
        setSaving(true); setErr('');
        try {
            // swot_id só vai quando o registo nasce de uma linha da SWOT.
            const payload = { ...form, ano, swot_id: risco?.swot_id || null };
            if (novo) await api.post('/sistema-integrado/riscos', payload);
            else await api.put(`/sistema-integrado/riscos/${risco.id}`, payload);
            onSaved(); onClose();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao guardar');
        } finally { setSaving(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>{novo ? `Novo risco/oportunidade (${ano})` : 'Editar registo'}</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>

                <div style={grid3}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Tipo</label>
                        <select value={form.tipo} onChange={e => upd('tipo', e.target.value)} style={{ ...inp, width: '100%' }}>
                            {Object.entries(TIPOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Origem</label>
                        <select value={form.origem} onChange={e => upd('origem', e.target.value)} style={{ ...inp, width: '100%' }}>
                            <option value="">—</option>
                            <option value="interna">Interna</option>
                            <option value="externa">Externa</option>
                        </select>
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Decisão (critérios RG.06)</label>
                        <select value={form.decisao} onChange={e => upd('decisao', e.target.value)} style={{ ...inp, width: '100%' }}>
                            <option value="">—</option>
                            {Object.entries(DECISOES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                    </div>
                </div>
                <div style={grid2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Processo</label>
                        <input value={form.processo} onChange={e => upd('processo', e.target.value)} placeholder="ex.: Comercial, Implementação" style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Contexto</label>
                        <input value={form.contexto} onChange={e => upd('contexto', e.target.value)} placeholder="ex.: Político-Legal, Económico" style={{ ...inp, width: '100%' }} />
                    </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Descrição do risco / oportunidade *</label>
                    <textarea value={form.descricao} onChange={e => upd('descricao', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Medidas adicionais / ações</label>
                    <textarea value={form.acoes} onChange={e => upd('acoes', e.target.value)} rows={3} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                <div style={grid3}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Planeamento</label>
                        <input value={form.planeamento} onChange={e => upd('planeamento', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Prazo</label>
                        <input type="date" value={form.prazo} onChange={e => upd('prazo', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Monitorização (fonte)</label>
                        <input value={form.monitorizacao} onChange={e => upd('monitorizacao', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Recursos</label>
                    <textarea value={form.recursos} onChange={e => upd('recursos', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                <div style={grid2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Eficácia (avaliação)</label>
                        <select value={form.eficacia} onChange={e => upd('eficacia', e.target.value)} style={{ ...inp, width: '100%' }}>
                            <option value="">—</option>
                            {form.eficacia && !['Eficaz', 'Não Eficaz'].includes(form.eficacia) && (
                                <option value={form.eficacia}>{form.eficacia}</option>
                            )}
                            <option value="Eficaz">Eficaz</option>
                            <option value="Não Eficaz">Não Eficaz</option>
                        </select>
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Observações</label>
                        <textarea value={form.observacoes} onChange={e => upd('observacoes', e.target.value)}
                            ref={ajustarAltura} onInput={e => ajustarAltura(e.target)}
                            rows={4} style={{ ...inp, width: '100%', resize: 'vertical', lineHeight: 1.5, minHeight: '6.5em' }} />
                    </div>
                </div>

                {err && <div style={{ color: '#e53e3e', margin: '10px 0', fontSize: '0.85rem' }}>{err}</div>}
                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-primary="true" onClick={guardar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>{saving ? 'A guardar…' : 'Guardar'}</button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Acompanhamento ---------- */
function AcompanhamentoModal({ risco, onClose, onChanged }) {
    const [registos, setRegistos] = useState(risco.acompanhamento || []);
    const [periodo, setPeriodo] = useState('');
    const [valor, setValor] = useState('1');
    const [comentario, setComentario] = useState('');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    const adicionar = async () => {
        if (!periodo.trim()) { setErr('Indique o período (ex.: 3ºT).'); return; }
        setSaving(true); setErr('');
        try {
            const r = await api.post(`/sistema-integrado/riscos/${risco.id}/acompanhamento`, { periodo, valor, comentario });
            setRegistos(prev => [...prev, r.data]);
            setPeriodo(''); setComentario('');
            onChanged();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao registar');
        } finally { setSaving(false); }
    };

    const remover = async (id) => {
        if (!window.confirm('Eliminar este registo de acompanhamento?')) return;
        try {
            await api.delete(`/sistema-integrado/riscos/acompanhamento/${id}`);
            setRegistos(prev => prev.filter(m => m.id !== id));
            onChanged();
        } catch { alert('Erro ao eliminar'); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>Acompanhamento</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                <div style={{ color: '#64748b', fontSize: '0.88rem', marginBottom: 8 }}>{risco.descricao}</div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: 12 }}>
                    Escala RG.06: <strong>0</strong> = atraso na implementação · <strong>1</strong> = decorre como previsto · <strong>2</strong> = concluído
                </div>

                {registos.length === 0 ? <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Sem registos.</p> : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.87rem', marginBottom: 12 }}>
                        <thead>
                            <tr style={{ textAlign: 'left', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
                                <th style={{ padding: '4px 6px' }}>Período</th>
                                <th style={{ padding: '4px 6px' }}>Estado</th>
                                <th style={{ padding: '4px 6px' }}>Comentário</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {registos.map(m => {
                                const c = ACOMP_COR[m.valor] || { bg: '#f1f5f9', fg: '#334155' };
                                return (
                                    <tr key={m.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                                        <td style={{ padding: '5px 6px', fontWeight: 600 }}>{m.periodo}</td>
                                        <td style={{ padding: '5px 6px' }}>
                                            <span data-liquid-tone={liquidTone(c.bg)} data-liquid-badge="true" style={{ ...badge, background: c.bg, color: c.fg, fontSize: '0.75rem' }}>
                                                {m.valor} — {ACOMP_LABEL[m.valor] || m.valor}
                                            </span>
                                        </td>
                                        <td style={{ padding: '5px 6px', color: '#64748b' }}>{m.comentario || ''}</td>
                                        <td style={{ textAlign: 'right' }}>
                                            <button data-liquid-secondary="true" onClick={() => remover(m.id)} style={{ ...btnSec, padding: '2px 8px', fontSize: '0.75rem', color: '#e53e3e' }} aria-label="Apagar" title="Apagar"><DeleteIcon /></button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}

                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Período</label>
                        <input value={periodo} onChange={e => setPeriodo(e.target.value)} placeholder="ex.: 3ºT" style={{ ...inp, width: 90 }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Estado</label>
                        <select value={valor} onChange={e => setValor(e.target.value)} style={inp}>
                            <option value="0">0 — Atraso</option>
                            <option value="1">1 — Decorre</option>
                            <option value="2">2 — Concluído</option>
                        </select>
                    </div>
                    <div style={{ flex: 1, minWidth: 130 }}>
                        <label data-liquid-label="true" style={lbl}>Comentário</label>
                        <input value={comentario} onChange={e => setComentario(e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <button data-liquid-primary="true" onClick={adicionar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>+ Registar</button>
                </div>
                {err && <div style={{ color: '#e53e3e', marginTop: 8, fontSize: '0.85rem' }}>{err}</div>}
            </div>
        </LiquidDialog>
    );
}

/* ---------- Componente principal ---------- */
/* ---------- Vista SWOT ---------- */
function SwotVista({ ano, anosExistentes, onGerarRO }) {
    const [linhas, setLinhas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editar, setEditar] = useState(null);
    const [duplicando, setDuplicando] = useState(false);

    const carregar = useCallback(async () => {
        setLoading(true);
        try { const r = await api.get('/sistema-integrado/swot', { params: { ano } }); setLinhas(r.data || []); }
        catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [ano]);
    useEffect(() => { carregar(); }, [carregar]);

    const anoAnterior = anosExistentes.filter(a => a < ano).sort((x, y) => y - x)[0] || null;

    const duplicar = async () => {
        if (!anoAnterior) return;
        if (!window.confirm(`Copiar a SWOT de ${anoAnterior} para ${ano}, para depois rever?`)) return;
        setDuplicando(true);
        try {
            const r = await api.post('/sistema-integrado/swot/duplicar-ano', { de: anoAnterior, para: ano });
            alert(r.data.message); carregar();
        } catch (e) { alert(e.response?.data?.error || 'Erro ao copiar'); }
        finally { setDuplicando(false); }
    };

    const eliminar = async (l) => {
        if (!window.confirm(`Eliminar "${l.descricao}"?`)) return;
        try { await api.delete(`/sistema-integrado/swot/${l.id}`); carregar(); }
        catch { alert('Erro ao eliminar'); }
    };

    // Duplicados vindos do RG.06 — assinalados, não removidos por conta própria.
    const contagem = {};
    linhas.forEach(l => { const k = `${l.quadrante}|${l.descricao}`; contagem[k] = (contagem[k] || 0) + 1; });

    if (loading) return <p style={{ color: '#718096' }}>A carregar…</p>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
                    Análise do contexto da organização (RG.06). As decisões nas oportunidades e
                    ameaças seguem os critérios da vista «Critérios».
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                    {linhas.length === 0 && anoAnterior && (
                        <button data-liquid-secondary="true" onClick={duplicar} disabled={duplicando} style={btnSec}>
                            <LiquidText>{duplicando ? 'A copiar…' : `⧉ Copiar SWOT de ${anoAnterior}`}</LiquidText>
                        </button>
                    )}
                    <button data-liquid-primary="true" onClick={() => setEditar({})} style={btnPri}>+ Nova linha</button>
                </div>
            </div>

            {linhas.length === 0 ? (
                <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>
                    Sem SWOT em {ano}. Copie a do ano anterior para rever, ou crie de novo.
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
                    {QUADRANTES.map(q => {
                        const doQuad = linhas.filter(l => l.quadrante === q.v);
                        return (
                            <div key={q.v} style={{ background: q.bg, border: `1px solid ${q.bd}`, borderRadius: 12, padding: 14 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                                    <strong style={{ fontSize: '0.95rem' }}><LiquidText>{q.icone}</LiquidText> {q.l}</strong>
                                    <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                                        origem {q.origem.toLowerCase()} · {doQuad.length}
                                    </span>
                                </div>
                                {doQuad.length === 0 && <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>—</div>}
                                {doQuad.map(l => {
                                    const dup = contagem[`${l.quadrante}|${l.descricao}`] > 1;
                                    const di = DECISOES[l.decisao];
                                    return (
                                        <div key={l.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 10px', marginBottom: 6 }}>
                                            <div style={{ fontSize: '0.83rem' }}>{l.descricao}</div>
                                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 5, flexWrap: 'wrap' }}>
                                                {di && <span data-liquid-tone={liquidTone(di.bg)} data-liquid-badge="true" style={{ ...badge, fontSize: '0.68rem', background: di.bg, color: di.fg }}><LiquidText>{di.label}</LiquidText></span>}
                                                {/* A auditoria exige que os quatro quadrantes sejam avaliados
                                                    como R&O — este selo mostra os que ainda não foram. */}
                                                {l.risco_id
                                                    ? <span data-liquid-tone={liquidTone('#dcfce7')} data-liquid-badge="true" style={{ ...badge, fontSize: '0.66rem', background: '#dcfce7', color: '#166534' }}>avaliado</span>
                                                    : <span data-liquid-tone={liquidTone('#fef3c7')} data-liquid-badge="true" style={{ ...badge, fontSize: '0.66rem', background: '#fef3c7', color: '#92400e' }}>por avaliar</span>}
                                                {dup && (
                                                    <span data-liquid-tone={liquidTone('#fef3c7')} data-liquid-badge="true" title="Aparece duas vezes neste quadrante, tal como no RG.06 original"
                                                        style={{ ...badge, fontSize: '0.66rem', background: '#fef3c7', color: '#92400e' }}>duplicado</span>
                                                )}
                                                <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                                                    {!l.risco_id && (
                                                        <button data-liquid-secondary="true" onClick={() => onGerarRO(l)} title="Criar o risco/oportunidade a partir desta linha"
                                                            style={{ ...btnSec, ...mini, marginLeft: 0 }}>→ R&O</button>
                                                    )}
                                                    <button data-liquid-secondary="true" onClick={() => setEditar(l)} style={{ ...btnSec, ...mini, marginLeft: 0 }}><LiquidText>✎</LiquidText></button>
                                                    <button data-liquid-secondary="true" onClick={() => eliminar(l)} style={{ ...btnSec, ...mini, marginLeft: 0, color: '#dc2626' }}><DeleteIcon /></button>
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            )}

            {editar && (
                <SwotLinhaModal linha={editar.id ? editar : null} ano={ano}
                    onClose={() => setEditar(null)} onSaved={carregar} />
            )}
        </div>
    );
}

/* ---------- Vista Critérios (RG.06, folha 3) ---------- */
function CriteriosVista() {
    /* O endpoint passou a devolver três blocos — decisão, significância e as
       regras de coerência entre elas — em vez do array simples que devolvia
       antes. O fallback mantém o ecrã funcional se a resposta vier na forma
       antiga. */
    const [criterios, setCriterios] = useState([]);
    const [significancia, setSignificancia] = useState([]);
    const [coerencia, setCoerencia] = useState([]);
    useEffect(() => {
        api.get('/sistema-integrado/swot/criterios').then(r => {
            const d = r.data || {};
            setCriterios(Array.isArray(d) ? d : (d.decisao || []));
            setSignificancia(d.significancia || []);
            setCoerencia(d.coerencia || []);
        }).catch(() => { });
    }, []);

    const CORES = {
        significativo: { bg: '#fee2e2', fg: '#991b1b' },
        moderado: { bg: '#fef3c7', fg: '#92400e' },
        nao_significativo: { bg: '#f1f5f9', fg: '#475569' },
    };

    return (
        <div>
            {significancia.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                    <h4 style={{ margin: '0 0 4px', fontSize: '0.86rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: '#475569' }}>
                        Significância
                    </h4>
                    <p style={{ margin: '0 0 10px', color: '#64748b', fontSize: '0.85rem' }}>
                        Quanto é que o fator importa. Definida na sequência da OM-2025-001, que pediu
                        para clarificar estes critérios. É eixo independente da decisão: um fator pode
                        ser significativo e ainda assim manter-se, quando não é controlável.
                    </p>
                    <div style={{ display: 'grid', gap: 10 }}>
                        {significancia.map(s => {
                            const co = CORES[s.valor] || CORES.nao_significativo;
                            return (
                                <div key={s.valor} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' }}>
                                    <span data-liquid-tone={liquidTone(co.bg)} data-liquid-badge="true" style={{ ...badge, background: co.bg, color: co.fg }}><LiquidText>{s.label}</LiquidText></span>
                                    <div style={{ fontSize: '0.84rem', color: '#334155', marginTop: 7 }}>{s.criterio}</div>
                                    <div style={{ fontSize: '0.79rem', color: '#64748b', marginTop: 5, fontStyle: 'italic' }}>{s.consequencia}</div>
                                </div>
                            );
                        })}
                    </div>
                    {coerencia.length > 0 && (
                        <div data-liquid-notice="true" data-liquid-tone={liquidTone('#eef2ff')} style={{ marginTop: 12, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 10, padding: '11px 14px' }}>
                            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#3730a3', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 6 }}>
                                Coerência entre significância e decisão
                            </div>
                            <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.83rem', color: '#3730a3' }}>
                                {coerencia.map((x, i) => <li key={i} style={{ marginBottom: 3 }}>{x}</li>)}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            <h4 style={{ margin: '0 0 4px', fontSize: '0.86rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: '#475569' }}>
                Decisão
            </h4>
            <p style={{ margin: '0 0 14px', color: '#64748b', fontSize: '0.85rem' }}>
                Critérios de decisão de implementação de ações (RG.06). É contra isto que se
                decide prosseguir, mitigar, partilhar ou manter.
            </p>
            {['oportunidade', 'risco'].map(alvo => (
                <div key={alvo} style={{ marginBottom: 18 }}>
                    <h4 style={{ margin: '0 0 8px', fontSize: '0.86rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: '#475569' }}>
                        {alvo === 'oportunidade' ? 'Oportunidades' : 'Riscos'}
                    </h4>
                    <div style={{ display: 'grid', gap: 10 }}>
                        {criterios.filter(c => c.aplica === alvo).map(c => {
                            const di = DECISOES[c.decisao] || {};
                            return (
                                <div key={c.decisao} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' }}>
                                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
                                        <span data-liquid-tone={liquidTone(di.bg || '#f1f5f9')} data-liquid-badge="true" style={{ ...badge, background: di.bg || '#f1f5f9', color: di.fg || '#475569' }}>
                                            {di.label || c.decisao}
                                        </span>
                                        <span style={{ fontSize: '0.76rem', color: '#94a3b8' }}>{c.acao}</span>
                                    </div>
                                    <div style={{ fontSize: '0.84rem', color: '#334155', whiteSpace: 'pre-line' }}>{c.criterio}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}

const RiscosOportunidades = () => {
    const anoAtual = new Date().getFullYear();
    const [ano, setAno] = useState(anoAtual);
    const [anosExistentes, setAnosExistentes] = useState([]);
    const [riscos, setRiscos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tipoFiltro, setTipoFiltro] = useState('');
    const [pesquisa, setPesquisa] = useState('');
    const [editar, setEditar] = useState(null);
    const [acomp, setAcomp] = useState(null);
    const [duplicando, setDuplicando] = useState(false);
    // As três vistas do RG.06: SWOT, registo dos R&O e critérios de decisão.
    const [vista, setVista] = useState('riscos');

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const [r, ra] = await Promise.all([
                api.get('/sistema-integrado/riscos', { params: { ano } }),
                api.get('/sistema-integrado/riscos/anos'),
            ]);
            setRiscos(r.data || []);
            setAnosExistentes(Array.isArray(ra.data) ? ra.data : []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [ano]);
    useEffect(() => { carregar(); }, [carregar]);

    const anos = [];
    for (let a = anoAtual + 1; a >= anoAtual - 4; a--) anos.push(a);
    anosExistentes.forEach(a => { if (!anos.includes(a)) anos.push(a); });
    anos.sort((x, y) => y - x);

    const eliminar = async (o) => {
        if (!window.confirm(`Eliminar "${o.descricao}" e o seu acompanhamento?`)) return;
        try { await api.delete(`/sistema-integrado/riscos/${o.id}`); carregar(); }
        catch { alert('Erro ao eliminar'); }
    };

    const atualizarEficacia = async (o, valor) => {
        try {
            await api.put(`/sistema-integrado/riscos/${o.id}/eficacia`, { eficacia: valor || null });
            setRiscos(prev => prev.map(r => r.id === o.id ? { ...r, eficacia: valor } : r));
        } catch { alert('Erro ao atualizar a eficácia'); }
    };

    const duplicarAnoAnterior = async () => {
        const de = ano - 1;
        if (!window.confirm(`Copiar os riscos/oportunidades de ${de} para ${ano} (sem acompanhamento)?`)) return;
        setDuplicando(true);
        try {
            const r = await api.post('/sistema-integrado/riscos/duplicar-ano', { de, para: ano });
            alert(r.data.message);
            carregar();
        } catch (e) { alert(e.response?.data?.error || 'Erro ao duplicar'); }
        finally { setDuplicando(false); }
    };

    let visiveis = tipoFiltro ? riscos.filter(r => r.tipo === tipoFiltro) : riscos;
    if (pesquisa.trim()) {
        const t = pesquisa.trim().toLowerCase();
        visiveis = visiveis.filter(r =>
            (r.descricao || '').toLowerCase().includes(t) || (r.processo || '').toLowerCase().includes(t) ||
            (r.contexto || '').toLowerCase().includes(t) || (r.acoes || '').toLowerCase().includes(t));
    }
    const nRiscos = riscos.filter(r => r.tipo === 'risco').length;
    const nOport = riscos.filter(r => r.tipo === 'oportunidade').length;

    // No RG.06 a SWOT, o registo e os critérios são três folhas do mesmo
    // ficheiro. Aqui são três vistas do mesmo separador, pela mesma razão.
    const subnav = (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 18 }}>
            {[
                ['swot', '🧭', 'SWOT', 'Análise do contexto da organização'],
                ['riscos', '⚖️', 'Riscos e Oportunidades', 'O registo, com acompanhamento e eficácia'],
                ['criterios', '📐', 'Critérios', 'O que fundamenta cada decisão'],
            ].map(([k, icone, titulo, desc]) => {
                const ativo = vista === k;
                return (
                    <button data-liquid-choice="true" aria-pressed={ativo} key={k} onClick={() => setVista(k)} title={desc}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
                            padding: '12px 14px', borderRadius: 12, cursor: 'pointer',
                            border: `1px solid ${ativo ? '#3182ce' : '#e2e8f0'}`,
                            background: ativo ? '#eff6ff' : '#fff',
                            boxShadow: ativo ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
                        }}>
                        <span style={{ fontSize: '1.7rem', lineHeight: 1 }}><LiquidText>{icone}</LiquidText></span>
                        <span style={{ minWidth: 0 }}>
                            <span style={{ display: 'block', fontWeight: 700, fontSize: '0.94rem', color: ativo ? '#1e40af' : '#1e293b' }}><LiquidText>{titulo}</LiquidText></span>
                            <span style={{ display: 'block', fontSize: '0.74rem', color: '#94a3b8', marginTop: 2 }}>{desc}</span>
                        </span>
                    </button>
                );
            })}
        </div>
    );

    const seletorAno = (
        <div>
            <label data-liquid-label="true" style={lbl}>Ano</label>
            <select value={ano} onChange={e => setAno(parseInt(e.target.value, 10))} style={inp}>
                {anos.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
        </div>
    );

    if (vista === 'criterios') {
        return <div>{subnav}<CriteriosVista /></div>;
    }
    if (vista === 'swot') {
        return (
            <div>
                {subnav}
                <div style={{ marginBottom: 14 }}>{seletorAno}</div>
                <SwotVista ano={ano} anosExistentes={anosExistentes}
                    onGerarRO={(l) => setEditar({
                        swot_id: l.id,
                        descricao: l.descricao,
                        // Natureza define o tipo; quadrante define a origem.
                        tipo: ['fraqueza', 'ameaca'].includes(l.quadrante) ? 'risco' : 'oportunidade',
                        decisao: l.decisao || '',
                        origem: ['forca', 'fraqueza'].includes(l.quadrante) ? 'interna' : 'externa',
                    })} />
                {/* Sem id, o RiscoModal trata-o como novo mas já com os campos
                    herdados da linha da SWOT. Ao gravar, salta para o registo. */}
                {editar && (
                    <RiscoModal risco={editar} ano={ano}
                        onClose={() => setEditar(null)} onSaved={() => { carregar(); setVista('riscos'); }} />
                )}
            </div>
        );
    }

    return (
        <div>
            {subnav}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    {seletorAno}
                    <div>
                        <label data-liquid-label="true" style={lbl}>Tipo</label>
                        <select value={tipoFiltro} onChange={e => setTipoFiltro(e.target.value)} style={inp}>
                            <option value="">Todos</option>
                            <option value="risco">Riscos</option>
                            <option value="oportunidade">Oportunidades</option>
                        </select>
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Pesquisar</label>
                        <input value={pesquisa} onChange={e => setPesquisa(e.target.value)} placeholder="Descrição, processo…" style={{ ...inp, width: 200 }} />
                    </div>
                    {riscos.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, paddingBottom: 4 }}>
                            <span data-liquid-tone={liquidTone(TIPOS.risco.bg)} data-liquid-badge="true" style={{ ...badge, background: TIPOS.risco.bg, color: TIPOS.risco.fg }}>{nRiscos} riscos</span>
                            <span data-liquid-tone={liquidTone(TIPOS.oportunidade.bg)} data-liquid-badge="true" style={{ ...badge, background: TIPOS.oportunidade.bg, color: TIPOS.oportunidade.fg }}>{nOport} oportunidades</span>
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    {riscos.length === 0 && !loading && (
                        <button data-liquid-secondary="true" onClick={duplicarAnoAnterior} disabled={duplicando} style={btnSec}>
                            <LiquidText>{duplicando ? 'A copiar…' : `⧉ Copiar de ${ano - 1}`}</LiquidText>
                        </button>
                    )}
                    <button data-liquid-primary="true" onClick={() => setEditar({})} style={btnPri}>+ Novo registo</button>
                </div>
            </div>

            {loading ? <p style={{ color: '#718096' }}>A carregar…</p>
                : visiveis.length === 0 ? (
                    <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>
                        {riscos.length === 0 ? `Sem riscos/oportunidades em ${ano}. Crie um novo ou copie do ano anterior.` : 'Nenhum registo corresponde aos filtros.'}
                    </div>
                ) : (
                    <div className="table-responsive" style={{ backgroundColor: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
                        <table className="standard-table">
                            <thead>
                                <tr>
                                    <th>Processo</th>
                                    <th>Descrição</th>
                                    <th>Origem</th>
                                    <th>Tipo</th>
                                    <th>Significância</th>
                                    <th>Decisão</th>
                                    <th>Prazo</th>
                                    <th>Acompanhamento</th>
                                    <th>Eficácia</th>
                                    <th style={{ textAlign: 'right' }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visiveis.map(o => {
                                    const tp = TIPOS[o.tipo] || TIPOS.risco;
                                    const dc = o.decisao ? DECISOES[o.decisao] : null;
                                    return (
                                        <tr key={o.id}>
                                            <td style={{ whiteSpace: 'nowrap', color: '#475569' }}>{o.processo || '—'}</td>
                                            <td style={{ fontWeight: 600, maxWidth: 320 }} title={o.acoes || ''}>{o.descricao}</td>
                                            <td style={{ textTransform: 'capitalize' }}>{o.origem || '—'}</td>
                                            <td><span data-liquid-tone={liquidTone(tp.bg)} data-liquid-badge="true" style={{ ...badge, background: tp.bg, color: tp.fg }}><LiquidText>{tp.label}</LiquidText></span></td>
                                            <td>
                                                {(() => {
                                                    const sg = SIGNIFICANCIA[o.significancia];
                                                    return sg
                                                        ? <span data-liquid-tone={liquidTone(sg.bg)} data-liquid-badge="true" style={{ ...badge, background: sg.bg, color: sg.fg }}><LiquidText>{sg.label}</LiquidText></span>
                                                        : <span style={{ color: '#cbd5e1' }}>—</span>;
                                                })()}
                                            </td>
                                            <td>{dc ? <span data-liquid-tone={liquidTone(dc.bg)} data-liquid-badge="true" style={{ ...badge, background: dc.bg, color: dc.fg }}><LiquidText>{dc.label}</LiquidText></span> : '—'}</td>
                                            <td style={{ whiteSpace: 'nowrap' }}>{fmtData(o.prazo)}</td>
                                            <td>
                                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 160 }}>
                                                    {(o.acompanhamento || []).length === 0 ? <span style={{ color: '#cbd5e1' }}>—</span>
                                                        : o.acompanhamento.map(m => {
                                                            const c = ACOMP_COR[m.valor] || { bg: '#f1f5f9', fg: '#334155' };
                                                            return (
                                                                <span data-liquid-tone={liquidTone(c.bg)} data-liquid-badge="true" key={m.id} title={`${ACOMP_LABEL[m.valor] || m.valor}${m.comentario ? ' — ' + m.comentario : ''}`}
                                                                    style={{ ...badge, background: c.bg, color: c.fg, fontSize: '0.72rem' }}>
                                                                    {m.periodo}: {m.valor}
                                                                </span>
                                                            );
                                                        })}
                                                </div>
                                            </td>
                                            <td>
                                                {(() => {
                                                    const padrao = ['Eficaz', 'Não Eficaz'];
                                                    const legado = o.eficacia && !padrao.includes(o.eficacia) ? o.eficacia : null;
                                                    const cor = o.eficacia === 'Eficaz' ? { background: '#dcfce7', color: '#166534' }
                                                        : o.eficacia === 'Não Eficaz' ? { background: '#fee2e2', color: '#991b1b' }
                                                        : { background: '#fff', color: '#334155' };
                                                    return (
                                                        <select
                                                            value={o.eficacia || ''}
                                                            onChange={e => atualizarEficacia(o, e.target.value)}
                                                            title={legado ? `Avaliação anterior: ${legado}` : 'Avaliação da eficácia'}
                                                            style={{ ...inp, padding: '4px 8px', fontSize: '0.8rem', fontWeight: 600, border: '1px solid #cbd5e1', ...cor }}
                                                        >
                                                            <option value="">—</option>
                                                            {legado && <option value={legado}>{String(legado).length > 24 ? String(legado).slice(0, 24) + '…' : legado}</option>}
                                                            <option value="Eficaz">Eficaz</option>
                                                            <option value="Não Eficaz">Não Eficaz</option>
                                                        </select>
                                                    );
                                                })()}
                                            </td>
                                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                <button data-liquid-primary="true" onClick={() => setAcomp(o)} style={{ ...btnPri, ...mini }} title="Acompanhamento periódico">Acomp.</button>
                                                <button data-liquid-secondary="true" onClick={() => setEditar({ risco: o })} style={{ ...btnSec, ...mini }} title="Editar">Editar</button>
                                                <button data-liquid-secondary="true" onClick={() => eliminar(o)} style={{ ...btnSec, ...mini, color: '#e53e3e' }} title="Eliminar"><DeleteIcon /></button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

            {editar && <RiscoModal risco={editar.risco} ano={ano} onClose={() => setEditar(null)} onSaved={carregar} />}
            {acomp && <AcompanhamentoModal risco={acomp} onClose={() => setAcomp(null)} onChanged={carregar} />}
        </div>
    );
};

/* ---------- estilos ---------- */
const lbl = { display: 'block', fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' };
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const mini = { padding: '5px 10px', marginLeft: 6, fontSize: '0.78rem' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 22, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const modalHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 };
const grid3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 };

export default RiscosOportunidades;
